import type {
  AxialCoord,
  CubeCoord,
  PixelCoord,
  HexDirection,
} from '@/types/hex.types';
import { HEX_SIZE } from '@/utils/constants';

/**
 * Direction vectors for pointy-top hex neighbors
 */
const DIRECTION_VECTORS: Record<number, AxialCoord> = {
  0: { q: 1, r: -1 }, // NorthEast
  1: { q: 1, r: 0 }, // East
  2: { q: 0, r: 1 }, // SouthEast
  3: { q: -1, r: 1 }, // SouthWest
  4: { q: -1, r: 0 }, // West
  5: { q: 0, r: -1 }, // NorthWest
};

/**
 * Convert axial to cube coordinates
 */
export function axialToCube(axial: AxialCoord): CubeCoord {
  return {
    q: axial.q,
    r: axial.r,
    s: -axial.q - axial.r,
  };
}

/**
 * Convert cube to axial coordinates
 */
export function cubeToAxial(cube: CubeCoord): AxialCoord {
  return {
    q: cube.q,
    r: cube.r,
  };
}

/**
 * Convert axial coordinates to pixel position (pointy-top)
 */
export function axialToPixel(axial: AxialCoord, size: number = HEX_SIZE): PixelCoord {
  const x = size * (Math.sqrt(3) * axial.q + (Math.sqrt(3) / 2) * axial.r);
  const y = size * ((3 / 2) * axial.r);
  return { x, y };
}

/**
 * Convert pixel position to axial coordinates (pointy-top)
 */
export function pixelToAxial(pixel: PixelCoord, size: number = HEX_SIZE): AxialCoord {
  const q = ((Math.sqrt(3) / 3) * pixel.x - (1 / 3) * pixel.y) / size;
  const r = ((2 / 3) * pixel.y) / size;
  return axialRound({ q, r });
}

/**
 * Round fractional axial coordinates to nearest hex
 */
export function axialRound(axial: AxialCoord): AxialCoord {
  const cube = axialToCube(axial);
  return cubeToAxial(cubeRound(cube));
}

/**
 * Round fractional cube coordinates to nearest hex
 */
export function cubeRound(cube: CubeCoord): CubeCoord {
  let q = Math.round(cube.q);
  let r = Math.round(cube.r);
  let s = Math.round(cube.s);

  const qDiff = Math.abs(q - cube.q);
  const rDiff = Math.abs(r - cube.r);
  const sDiff = Math.abs(s - cube.s);

  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  } else {
    s = -q - r;
  }

  return { q, r, s };
}

/**
 * Calculate distance between two hex positions
 */
export function hexDistance(a: AxialCoord, b: AxialCoord): number {
  const ac = axialToCube(a);
  const bc = axialToCube(b);
  return Math.max(
    Math.abs(ac.q - bc.q),
    Math.abs(ac.r - bc.r),
    Math.abs(ac.s - bc.s)
  );
}

/**
 * Get neighbor coordinate in a direction
 */
export function getNeighbor(coord: AxialCoord, direction: HexDirection): AxialCoord {
  const dir = DIRECTION_VECTORS[direction];
  return {
    q: coord.q + dir.q,
    r: coord.r + dir.r,
  };
}

/**
 * Get all six neighbors of a hex
 */
export function getNeighbors(coord: AxialCoord): AxialCoord[] {
  return Object.values(DIRECTION_VECTORS).map((dir) => ({
    q: coord.q + dir.q,
    r: coord.r + dir.r,
  }));
}

/**
 * Create a unique string key for a hex coordinate
 */
export function coordToKey(coord: AxialCoord): string {
  return `${coord.q},${coord.r}`;
}

/**
 * Parse a coordinate key back to axial coordinates
 */
export function keyToCoord(key: string): AxialCoord {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
}

/**
 * Check if two coordinates are equal
 */
export function coordsEqual(a: AxialCoord, b: AxialCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

/**
 * Get hexes in a line between two points
 */
export function hexLine(a: AxialCoord, b: AxialCoord): AxialCoord[] {
  const n = hexDistance(a, b);
  if (n === 0) return [a];

  const results: AxialCoord[] = [];
  const ac = axialToCube(a);
  const bc = axialToCube(b);

  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const cube: CubeCoord = {
      q: ac.q + (bc.q - ac.q) * t,
      r: ac.r + (bc.r - ac.r) * t,
      s: ac.s + (bc.s - ac.s) * t,
    };
    results.push(cubeToAxial(cubeRound(cube)));
  }

  return results;
}

/**
 * Get hexes within a range
 */
export function hexRange(center: AxialCoord, range: number): AxialCoord[] {
  const results: AxialCoord[] = [];

  for (let q = -range; q <= range; q++) {
    for (let r = Math.max(-range, -q - range); r <= Math.min(range, -q + range); r++) {
      results.push({
        q: center.q + q,
        r: center.r + r,
      });
    }
  }

  return results;
}

/**
 * Mirror a coordinate vertically for visual display
 * Used to flip the grid so local player always appears at bottom
 *
 * The grid uses odd-r offset coordinates converted to axial.
 * To mirror visually, we need to flip in offset space (preserving column)
 * then convert back to axial.
 *
 * Self-inverse: mirror(mirror(coord)) = coord
 */
export function mirrorCoord(coord: AxialCoord, gridHeight: number = 8): AxialCoord {
  // Convert axial to offset column: col = q + floor(r/2)
  const col = coord.q + Math.floor(coord.r / 2);
  // Flip the row
  const mirroredRow = gridHeight - 1 - coord.r;
  // Convert back to axial: q = col - floor(row/2)
  const mirroredQ = col - Math.floor(mirroredRow / 2);
  return { q: mirroredQ, r: mirroredRow };
}
